import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { FormProvider, RHFFileField, RHFSelectField } from '../../common/hook-form'
import * as XLSX from 'xlsx/xlsx.mjs';
import { apiConstants, postMultipart } from '../../common/constants';
import { LoadingButton } from '@mui/lab';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadSampleFile from '../../components/DownloadSampleFile';
import { useSnackbar } from 'notistack';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import PageTitle from '../../components/PageTitle';
import { FieldLabel } from '../../common/FilterBar';

export default function BulkImageUpload() {
    const methods = useForm({
        resolver: yupResolver(Yup.object().shape({
            courier: Yup.string().required("Please Select Type"),
            file: Yup.mixed().required("Please Select a File"),
        })),
        defaultValues: { courier: "", file: "" }
    });
    const { handleSubmit, reset } = methods;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const option = [
        { label: "Courier Images", value: "COURIER" },
        { label: "Merchant Images", value: "MERCHANT" },
    ];
    const sampleName = 'IMAGE_URL';

    const onSubmit = (data) => {
        const type = data?.courier;
        const file = data.file[0];
        if (!file) return;
        try {
            const reader = new FileReader();
            reader.readAsBinaryString(file);
            reader.onload = async function (e) {
                setIsSubmitting(true);
                const workbook = XLSX.read(e.target.result, { type: 'binary' });
                let result = {};
                workbook.SheetNames.forEach((sheetName) => {
                    const roa = XLSX.utils.sheet_to_row_object_array(workbook.Sheets[sheetName]);
                    if (roa.length > 0) result[sheetName] = roa;
                });
                const sheetResult = result.Sheet1;
                for (let i = 0; i < sheetResult?.length; i++) {
                    const formData = new FormData();
                    formData.append('file1', sheetResult[i].URL1);
                    if (sheetResult[i].URL2) formData.append('file2', sheetResult[i].URL2);
                    if (sheetResult[i].URL3) formData.append('file3', sheetResult[i].URL3);
                    if (sheetResult[i].URL4) formData.append('file4', sheetResult[i].URL4);
                    if (sheetResult[i].URL5) formData.append('file5', sheetResult[i].URL5);
                    if (sheetResult[i].URL6) formData.append('file6', sheetResult[i].URL6);
                    await postMultipart(`${apiConstants.WEIGHT_DISCREPANCY_LINK_UPLOAD}/${sheetResult[i].awb_number}/${type}`, formData)
                        .then((res) => {
                            if (res.data.length > 0) {
                                enqueueSnackbar("Successfully uploaded!", { variant: 'success' });
                                reset({ file: null, courier: 'null' });
                            } else {
                                enqueueSnackbar(res.data.errorMsg, { variant: 'error' });
                            }
                        })
                        .catch((err) => {
                            enqueueSnackbar(err.response?.data || "Some error occurred", { variant: 'error' });
                            console.log(err);
                        });
                }
                setIsSubmitting(false);
            };
        } catch (err) { console.log(err); }
    };

    return (
        <>
            <PageTitle>Bulk Image Upload</PageTitle>

            <Card elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>Bulk Image Upload</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#9ca3af', mt: '2px' }}>
                        Upload an Excel file with image URLs to attach courier or merchant images to weight discrepancies in bulk
                    </Typography>
                </Box>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2} alignItems="flex-end">

                            <Grid item xs={12} sm={4}>
                                <FieldLabel>Image Type *</FieldLabel>
                                <RHFSelectField
                                    name='courier'
                                    label=""
                                    placeholder="Select Image Type"
                                    width='100%'
                                    size="small"
                                    displayEmpty
                                    options={option.map(v => ({ value: v.value, label: v.label }))}
                                />
                            </Grid>

                            <Grid item xs={12} sm={8}>
                                <FieldLabel>Upload File (Excel)</FieldLabel>
                                <RHFFileField name='file' fileType={['xlsx']} label="Click to upload Excel file with image URLs" />
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                    <LoadingButton
                                        loading={isSubmitting}
                                        variant="contained"
                                        size="small"
                                        startIcon={<UploadIcon />}
                                        type="submit"
                                        disableElevation
                                        sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
                                    >
                                        Upload
                                    </LoadingButton>
                                    <DownloadSampleFile fileName={sampleName} excel="excel" label="Sample" />
                                </Box>
                            </Grid>

                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>
        </>
    );
}
